import { useI18n } from '@ecom/i18n';
import { type ReactElement, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

const STORE_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M3.5 8 12 3.5 20.5 8v8L12 20.5 3.5 16V8Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

const TEAM_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="2" />
    <path
      d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M16 4.2a3 3 0 0 1 0 5.8M20 20c0-2.6-1.7-4.8-4-5.6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

function SettingsLinkCard({
  icon,
  title,
  description,
  to,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  to: string;
}): ReactElement {
  return (
    <Link
      to={to}
      className="flex items-start gap-3 rounded-xl2 border border-line bg-surface p-5 shadow-card transition-colors hover:border-accent/50"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accentSoft text-accentInk">
        {icon}
      </span>
      <span>
        <span className="block font-display text-base font-bold text-ink">{title}</span>
        <span className="mt-0.5 block text-sm text-inkFaint">{description}</span>
      </span>
    </Link>
  );
}

/**
 * "Paramètres" — un vrai hub, pas un second formulaire dupliquant ce qui
 * existe déjà : Boutique (`/store`, profil public + Santé de la boutique)
 * et Équipe & permissions (`/team`, rôles/accès réels) sont chacun un
 * écran complet depuis longtemps — les relier ici évite de répéter leur
 * contenu sous un autre nom.
 */
export function SettingsPage(): ReactElement {
  const { t } = useI18n();
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">{t('merchant.nav.settings')}</h1>
        <p className="text-sm text-inkFaint">{t('merchant.settings.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SettingsLinkCard
          icon={STORE_ICON}
          title={t('merchant.nav.store')}
          description={t('merchant.settings.storeCardDescription')}
          to="/store"
        />
        <SettingsLinkCard
          icon={TEAM_ICON}
          title={t('merchant.nav.team')}
          description={t('merchant.settings.teamCardDescription')}
          to="/team"
        />
      </div>
    </div>
  );
}
